﻿/** @jsxImportSource solid-js */

import {
  Component,
  createSignal,
  createEffect,
  onCleanup,
  Show,
  on,
} from 'solid-js';
import Button from '../Button/Button';
import { BtnClasses } from '../../types/common';

export interface ISnackBarMessage {
  text: string;
  type?: IColors;
  timeoutMilliseconds?: number;
  onClick?(): void;
  buttonText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callBack?(args?: any): void;
}

export const staticColors = {
  success: '#5E9331',
  error: '#C81922',
  warning: '#FFBE00',
  info: '#363545',
};

export type IColors = 'success' | 'error' | 'warning' | 'info';

export interface ConnectSnackbarProps {
  message?: ISnackBarMessage;
  color?: string;
}

const ConnectSnackbar: Component<ConnectSnackbarProps> = (props) => {
  const defaultTimeOut = 3200;
  const [visible, setVisible] = createSignal<boolean>(false);
  const [timer, setTimer] = createSignal<number>(0);

  const cancelTimeout = (): void => {
    const currentTimer = timer();
    if (currentTimer) {
      window.clearTimeout(currentTimer);
      setTimer(0);
    }
  };

  const hideSnackbar = (): void => {
    cancelTimeout();
    if (props.message?.callBack) {
      props.message.callBack();
    }
    setVisible(false);
  };

  const showSnackbar = (timeout?: number): void => {
    cancelTimeout();
    setVisible(true);
    const timeoutId = window.setTimeout(
      hideSnackbar,
      timeout ?? defaultTimeOut
    );
    setTimer(timeoutId);
  };

  const onClickMessage = (): void => {
    hideSnackbar();
    props.message?.onClick?.();
  };

  createEffect(
    on(
      () => props.message?.text,
      (text) => {
        if (text && props.message) {
          showSnackbar(props.message.timeoutMilliseconds);
        } else {
          hideSnackbar();
        }
      }
    )
  );

  onCleanup(() => {
    cancelTimeout();
  });

  const getDelay = (): number =>
    props.message?.timeoutMilliseconds ?? defaultTimeOut;

  return (
    <Show when={props.message && visible()}>
      <modus-wc-toast
        delay={getDelay()}
        position="top-end"
        onClick={onClickMessage}
      >
        <modus-wc-alert
          variant={
            (props.message?.type ?? 'info') as unknown as
              | 'info'
              | 'warning'
              | 'error'
              | 'success'
          }
          alert-title={props.message?.text}
          dismissible={false}
        >
          <Show when={props.message?.buttonText && props.message.callBack}>
            <div slot="button">
              <Button
                content={props.message?.buttonText}
                color={BtnClasses.Primary}
                size="small"
                onClick={() => props.message?.callBack?.()}
                buttonType="button"
              />
            </div>
          </Show>
        </modus-wc-alert>
      </modus-wc-toast>
    </Show>
  );
};

export default ConnectSnackbar;
